<div class="user-results-container">
  <!-- Stats Cards -->
  <div class="results-stats-row">
    <div class="stat-card">
      <div class="stat-icon total">
        <i class="fa fa-flag-checkered"></i>
      </div>
      <div class="stat-content">
        <span class="stat-value">{{ totalResults }}</span>
        <span class="stat-label">{{ 'userResults.totalResults' | translate }}</span>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon wins">
        <i class="fa fa-trophy"></i>
      </div>
      <div class="stat-content">
        <span class="stat-value">{{ wins }}</span>
        <span class="stat-label">{{ 'userResults.wins' | translate }}</span>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon podiums">
        <i class="fa fa-medal"></i>
      </div>
      <div class="stat-content">
        <span class="stat-value">{{ podiums }}</span>
        <span class="stat-label">{{ 'userResults.podiums' | translate }}</span>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon best">
        <i class="fa fa-arrow-up"></i>
      </div>
      <div class="stat-content">
        <span class="stat-value">{{ bestPosition || '-' }}</span>
        <span class="stat-label">{{ 'userResults.bestPosition' | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="results-filters">
    <div class="filter-group">
      <label class="filter-label">{{ 'userResults.filterByYear' | translate }}</label>
      <select class="form-select filter-select" [(ngModel)]="selectedYear">
        <option [ngValue]="null">{{ 'userResults.allYears' | translate }}</option>
        @for (year of availableYears; track year) {
          <option [ngValue]="year">{{ year }}</option>
        }
      </select>
    </div>
  </div>

  <!-- Loading State -->
  @if (loading) {
    <div class="results-loading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-3">{{ 'userResults.loadingResults' | translate }}</p>
    </div>
  }

  <!-- Empty State -->
  @if (!loading && results.length === 0) {
    <div class="results-empty">
      <div class="empty-icon">
        <i class="fa fa-trophy"></i>
      </div>
      <h4>{{ 'userResults.noResults' | translate }}</h4>
      <p>{{ 'userResults.noResultsHint' | translate }}</p>
    </div>
  }

  <!-- Results Grid -->
  @if (!loading && results.length > 0) {
    <div class="results-grid">
      @for (eventGroup of groupedResults; track eventGroup.event_id) {
        <div class="result-event-card" [class.expanded]="isEventExpanded(eventGroup.event_id)">
          <!-- Card Header (clickable to expand) -->
          <div class="card-header-section" (click)="toggleEventExpanded(eventGroup.event_id)">
            <div class="event-card-info">
              <div class="event-card-icon">
                <i class="fa fa-trophy"></i>
              </div>
              <div class="event-card-details">
                <h4 class="event-card-name">{{ eventGroup.event_name }}</h4>
                <div class="event-card-meta">
                  <span class="event-card-date">
                    <i class="fa fa-calendar-alt me-1"></i>
                    {{ formatDateDMYDots(eventGroup.event_date) }}
                  </span>
                  <span class="event-card-count">
                    <i class="fa fa-flag-checkered me-1"></i>
                    {{ eventGroup.results.length }} {{ 'userResults.classes' | translate }}
                  </span>
                </div>
              </div>
            </div>
            <div class="card-expand-indicator">
              <i class="fa fa-chevron-down" [class.rotated]="isEventExpanded(eventGroup.event_id)"></i>
            </div>
          </div>

          <!-- Preview Results (collapsed state) -->
          @if (!isEventExpanded(eventGroup.event_id)) {
            <div class="results-preview">
              @for (result of getPreviewResults(eventGroup); track result.result_id) {
                <div class="preview-result-item">
                  <span class="preview-position position-badge {{ getPositionClass(result.position) }}">
                    @if (result.position >= 1 && result.position <= 3) {
                      <i class="fa {{ getPositionIcon(result.position) }}"></i>
                    } @else {
                      {{ result.position || '-' }}
                    }
                  </span>
                  <span class="preview-class">{{ result.class_name }}</span>
                  <span class="preview-score">{{ getResultScore(result) }}</span>
                </div>
              }
              @if (getHiddenResultsCount(eventGroup) > 0) {
                <div class="preview-more">
                  <span class="more-badge">+{{ getHiddenResultsCount(eventGroup) }} {{ 'userResults.moreResults' | translate }}</span>
                </div>
              }
            </div>
          }

          <!-- Full Results (expanded state) -->
          @if (isEventExpanded(eventGroup.event_id)) {
            <div class="results-full">
              <div class="results-full-header">
                <a [routerLink]="['/event-details', eventGroup.event_id]" [queryParams]="{view: 'results'}" class="btn btn-sm btn-light-primary">
                  <i class="fa fa-external-link-alt me-1"></i>
                  {{ 'userResults.viewEventResults' | translate }}
                </a>
              </div>
              @for (result of eventGroup.results; track result.result_id) {
                <div class="result-row" [class.podium-result]="result.position >= 1 && result.position <= 3">
                  <div class="result-position">
                    <span class="position-badge {{ getPositionClass(result.position) }}">
                      @if (result.position >= 1 && result.position <= 3) {
                        <i class="fa {{ getPositionIcon(result.position) }}"></i>
                      } @else {
                        {{ result.position || '-' }}
                      }
                    </span>
                  </div>
                  <div class="result-details">
                    <div class="result-class">
                      <span class="class-name">{{ result.class_name }}</span>
                      <span class="participant-count">
                        {{ result.position }}/{{ result.total_participants }}
                      </span>
                    </div>
                    <div class="result-horse">
                      <i class="fa fa-horse text-muted me-1"></i>
                      {{ result.horse_name }}
                    </div>
                  </div>
                  <div class="result-score">
                    <span class="score-value">{{ getResultScore(result) }}</span>
                    @if (result.penalty_total > 0) {
                      <span class="penalty-indicator">+{{ result.penalty_total }}s</span>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
